import type { ReactNode } from "react";
import {
  Receipt,
  RotateCcw,
  FilePlus,
  CheckCircle2,
  Wallet,
  Layers,
  Check,
} from "lucide-react";
import { DetailTemplate } from "@/templates/DetailTemplate";
import {
  ImpactGrid,
  ImpactTile,
} from "@/portal/templates/ImpactDashboardTemplate";
import { Card } from "@/components/Card";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/money";

interface Allocation {
  amountMinor: number;
  goalName: string | null;
  projectName: string | null;
  goalStatus: string | null;
  contributedMinor: number;
  targetMinor: number;
}

interface Contribution {
  id: string;
  amountMinor: number;
  allocatedMinor: number;
  currency: string;
  status: string;
  entryType: string;
  contributedAt: string | null;
  createdAt: string | null;
  sourceName: string | null;
  allocations: Allocation[];
}

interface Props {
  contribution: Contribution;
}

const money = (minor: number, currency = "GBP") => formatMoney(minor / 100, currency);

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STEPS = [
  { key: "submitted", label: "Submitted", icon: <FilePlus />, description: "Received from your source" },
  { key: "allocated", label: "Allocated", icon: <CheckCircle2 />, description: "Earmarked to your goals" },
  { key: "confirmed", label: "Confirmed", icon: <CheckCircle2 />, description: "Validated with the project" },
  { key: "settled", label: "Settled", icon: <Wallet />, description: "Funds transferred" },
];
const ORDER: Record<string, number> = { submitted: 0, allocated: 1, confirmed: 2, settled: 3 };

export default function ContributionsShow({ contribution: c }: Props) {
  const isReversal = c.entryType === "reversal";
  const currentStep = ORDER[c.status] ?? -1;

  return (
    <DetailTemplate
      title={`Contribution ${c.id}`}
      subtitle={
        <>
          {c.sourceName ? `${c.sourceName} · ` : ""}
          {fmtDate(c.contributedAt)}
        </>
      }
      backTo="/contributions"
      backLabel="All contributions"
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Contributions", to: "/contributions" },
        { label: c.id },
      ]}
      leading={
        <span className="flex size-10 items-center justify-center rounded-full bg-accent-subtle text-accent-text [&_svg]:size-5">
          {isReversal ? <RotateCcw /> : <Receipt />}
        </span>
      }
      meta={
        <>
          <span className="text-lg font-semibold tracking-tight text-fg tabular">
            {money(c.amountMinor, c.currency)}
          </span>
          <StatusBadge status={c.status} />
          {isReversal && <Badge tone="danger">Reversal</Badge>}
        </>
      }
    >
      <ImpactGrid>
        {/* ── Lifecycle ───────────────────────────────────────────── */}
        <ImpactTile col={8} index={0}>
          <Card>
            <Card.Header
              title="Lifecycle"
              description="Where this contribution is in its journey to your projects."
            />
            <Card.Body>
              {isReversal ? (
                <div className="flex items-center gap-3 rounded-[var(--radius-md)] bg-danger-bg px-4 py-3 text-danger-fg">
                  <RotateCcw className="size-5 shrink-0" />
                  <p className="text-sm">
                    This entry reverses a previous contribution.
                  </p>
                </div>
              ) : (
                <ol className="flex flex-col gap-0 sm:flex-row sm:items-start sm:gap-0">
                  {STEPS.map((step, i) => {
                    const done = i < currentStep;
                    const active = i === currentStep;
                    return (
                      <li key={step.key} className="flex flex-1 gap-3 sm:flex-col sm:gap-2">
                        <div className="flex flex-col items-center sm:flex-row sm:w-full">
                          <span
                            className={cn(
                              "flex size-8 shrink-0 items-center justify-center rounded-full border [&_svg]:size-4",
                              done && "border-transparent bg-accent text-fg-on-accent",
                              active && "border-accent-border bg-accent-subtle text-accent-text",
                              !done && !active && "border-border-subtle bg-surface text-fg-subtle",
                            )}
                          >
                            {done ? <Check /> : step.icon}
                          </span>
                          {i < STEPS.length - 1 && (
                            <span
                              className={cn(
                                "my-1 h-6 w-px sm:my-0 sm:h-px sm:w-full sm:flex-1",
                                i < currentStep ? "bg-accent" : "bg-border-subtle",
                              )}
                            />
                          )}
                        </div>
                        <div className="pb-4 sm:pb-0 sm:pr-3">
                          <div
                            className={cn(
                              "text-sm font-medium",
                              active || done ? "text-fg" : "text-fg-muted",
                            )}
                          >
                            {step.label}
                          </div>
                          <div className="mt-0.5 text-xs text-fg-subtle">{step.description}</div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* ── Summary ─────────────────────────────────────────────── */}
        <ImpactTile col={4} index={1}>
          <Card className="h-full">
            <Card.Header title="Summary" />
            <Card.Body className="flex flex-col gap-3">
              <SummaryRow label="ID" value={<span className="font-mono text-xs">{c.id}</span>} />
              <SummaryRow label="Total amount" value={money(c.amountMinor, c.currency)} />
              <SummaryRow label="Allocated to you" value={money(c.allocatedMinor, c.currency)} />
              <SummaryRow label="Source" value={c.sourceName ?? "—"} />
              <SummaryRow label="Contributed" value={fmtDate(c.contributedAt)} />
              <SummaryRow label="Recorded" value={fmtDate(c.createdAt)} />
              <SummaryRow
                label="Type"
                value={<span className="capitalize">{c.entryType}</span>}
              />
            </Card.Body>
          </Card>
        </ImpactTile>

        {/* ── Allocations to your projects ────────────────────────── */}
        <ImpactTile col={12} index={2}>
          <Card>
            <Card.Header
              title="Allocations to your projects"
              description="How this contribution was split across your goals."
            />
            <Card.Body className={c.allocations.length === 0 ? "" : "p-0"}>
              {c.allocations.length === 0 ? (
                <EmptyState icon={<Layers />} title="No allocations to your projects" />
              ) : (
                <ul className="divide-y divide-border-subtle">
                  {c.allocations.map((a, i) => {
                    const pct =
                      a.targetMinor > 0
                        ? Math.min(100, Math.round((a.contributedMinor / a.targetMinor) * 100))
                        : 0;
                    return (
                      <li key={i} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-fg">
                            {a.goalName ?? "—"}
                          </div>
                          <div className="mt-0.5 truncate text-xs text-fg-muted">
                            {a.projectName ?? "—"}
                          </div>
                        </div>
                        <div className="sm:w-56">
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-accent"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="mt-1 text-[11px] text-fg-subtle tabular">
                            {money(a.contributedMinor, c.currency)} of {money(a.targetMinor, c.currency)} ({pct}%)
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-sm font-medium tabular sm:w-28">
                          {money(a.amountMinor, c.currency)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card.Body>
          </Card>
        </ImpactTile>
      </ImpactGrid>
    </DetailTemplate>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-xs text-fg-subtle">{label}</span>
      <span className="min-w-0 truncate text-right text-sm text-fg">{value}</span>
    </div>
  );
}
